import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { PublicKey } from '@hashgraph/sdk';
import { AxiosError } from 'axios';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Buffer } from 'buffer';
import { motion, AnimatePresence } from 'framer-motion';
import { createMultisigAccount } from '../../api/multisigAPI';
import { createMultisigAccount as createMultisigTx } from '../../services/TransactionService';
import { getAccountData } from '../../api/userAPI';
import { getActiveKey, clearKey } from '../../signing/KeyManager';
import { getConfig } from '../../utils/networkConfigStore';
import { LocalKeyStrategy } from '../../signing/LocalKeyStrategy';
import { useSigningConfirmation } from '../../providers/SigningConfirmation';
import { useToast } from '../../hooks/useToast';
import { getWalletErrorMessage } from '../../utils/walletErrors';
import { GlobalContext } from '../../providers/Global';
import { CreateAccount, Signer } from '../../types/account';
import { MultisigRoles, SCREEN_LG, SCREEN_SM } from '../../utils/constants';
import { ApplicationRoutes } from '../../utils/routes';
import AccountForm from './AccountForm';
import Menu from './components/Menu';
import SetThresholdForm from './SetThresholdForm';
import SignersForm from './SignersForm';
import SummaryForm from './SummaryForm';
import { useMediaQuery } from 'react-responsive';
import { ReactPortal } from 'hooks/usePortal';

const MotionFlex = motion(Flex);
const MotionBox = motion(Box);

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut', delay },
  }),
};

const stepTransition = {
  initial: (direction: number) => ({
    opacity: 0,
    x: direction > 0 ? 60 : -60,
  }),
  animate: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.35, ease: 'easeOut' },
  },
  exit: (direction: number) => ({
    opacity: 0,
    x: direction > 0 ? -60 : 60,
    transition: { duration: 0.2, ease: 'easeIn' },
  }),
};

const styles = {
  steps: (isMobile: boolean) => {
    if (isMobile) {
      return {
        position: "absolute",
        top: '24px',
        right: '24px',
        fontSize: '10px',
        color: 'rgba(0, 0, 0, 0.48)',
      }
    }
    return {
      margin: "auto",
    }
  }
};

export const getSignerAccount = (
  name: string = '',
  accountId: string = '',
  accountKey: string = ''
): Signer => {
  return {
    name,
    accountId,
    accountKey,
  }
}

const CreateAccountPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { requestSignConfirmation } = useSigningConfirmation();
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [nextButtonRoute, setNextButtonRoute] = useState(ApplicationRoutes.CreateAccount);
  const [backButtonRoute, setBackButtonRoute] = useState(ApplicationRoutes.SignersForm);
  const [missingSignersData, setMissingSignersData] = useState(true);
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(true);
  const {
    connection: { accountId, hashconnectConnectorInstance },
    connectedNetwork
  } = useContext(GlobalContext);

  const isScreenSmall = useMediaQuery({ query: `(max-width: ${SCREEN_SM})` });
  const isScreenLarge = useMediaQuery({ query: `(max-width: ${SCREEN_LG})` });

  const routeOrder: string[] = [
    ApplicationRoutes.CreateAccount,
    ApplicationRoutes.SignersForm,
    ApplicationRoutes.ThresholdForm,
    ApplicationRoutes.SummaryForm,
  ];
  const currentRouteIndex = routeOrder.indexOf(location.pathname);
  const prevRouteIndex = useRef(currentRouteIndex);
  const direction = useRef(1);

  if (currentRouteIndex !== prevRouteIndex.current) {
    direction.current = currentRouteIndex > prevRouteIndex.current ? 1 : -1;
    prevRouteIndex.current = currentRouteIndex;
  }

  const creatorAccount = useMemo(() => getSignerAccount(MultisigRoles.Creator, accountId), [accountId]);

  const [createAccountData, setCreateAccountData] = useState<CreateAccount>({
    name: '',
    network: connectedNetwork,
    accounts: [
      creatorAccount,
      getSignerAccount(),
    ],
    threshold: 1,
    transactionId: '',
  });

  const updateRouteData = useCallback(() => {
    switch (location.pathname) {
      case ApplicationRoutes.CreateAccount:
        setStep(1);
        setNextButtonRoute(ApplicationRoutes.SignersForm);
        break;

      case ApplicationRoutes.SignersForm:
        setStep(2);
        setBackButtonRoute(ApplicationRoutes.CreateAccount);
        setNextButtonRoute(ApplicationRoutes.ThresholdForm);
        break;

      case ApplicationRoutes.ThresholdForm:
        setStep(3);
        setBackButtonRoute(ApplicationRoutes.SignersForm);
        setNextButtonRoute(ApplicationRoutes.SummaryForm);
        break;

      case ApplicationRoutes.SummaryForm:
        setStep(4);
        setBackButtonRoute(ApplicationRoutes.ThresholdForm);
        break;

      default:
        return null;
    }
  }, [location.pathname]);

  const renderComponent = () => {
    switch (location.pathname) {
      case ApplicationRoutes.CreateAccount:
        return <AccountForm data={createAccountData} setData={setCreateAccountData} />;

      case ApplicationRoutes.SignersForm:
        return (
          <SignersForm
            data={createAccountData}
            setData={setCreateAccountData}
            setIsInvalid={setIsInvalid}
          />
        );

      case ApplicationRoutes.ThresholdForm:
        return <SetThresholdForm data={createAccountData} setData={setCreateAccountData} />;

      case ApplicationRoutes.SummaryForm:
        return <SummaryForm data={createAccountData} duplicateAddress={isDuplicate} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    setIsInvalid(false);
    if (!createAccountData.name) {
      setIsInvalid(true);
      return;
    }
    createAccountData?.accounts?.forEach((account: Signer) => {
      if (!account.name || !account.accountId) {
        setMissingSignersData(true);
        setIsInvalid(true);
        return;
      } else {
        setMissingSignersData(false);
      }
    });
  }, [createAccountData]);

  useEffect(() => {
    if (createAccountData?.accounts && createAccountData?.accounts?.length > 0) {
      const usedAddresses = createAccountData?.accounts?.map(account => account.accountId);
      const isDuplicate = usedAddresses?.some(function (item, idx) {
        return usedAddresses.indexOf(item) !== idx;
      });
      if (isDuplicate) {
        setIsDuplicate(true);
        return;
      }
      setIsDuplicate(false);
    }
  }, [createAccountData]);

  const mapAccountsData = async (): Promise<any> => {
    try {
      const mappedAccounts = createAccountData?.accounts?.map(async (account: Signer) => {
        const accountData = await getAccountData(account.accountId, connectedNetwork!);

        // Convert the hex key from Mirror Node to a PublicKey object, then back to DER string
        // This handles both Ed25519 and ECDSA keys properly
        let publicKey: any;
        try {
          // Try Ed25519 first (most common for Hedera)
          publicKey = PublicKey.fromBytesED25519(Buffer.from(accountData.key, 'hex'));
        } catch (e) {
          try {
            // If that fails, try ECDSA
            publicKey = PublicKey.fromBytesECDSA(Buffer.from(accountData.key, 'hex'));
          } catch (e2) {
            // If both fail, try to parse as DER-encoded string
            publicKey = PublicKey.fromString(accountData.key);
          }
        }

        return {
          ...account,
          accountKey: publicKey.toStringDer(),
        };
      });
      if (mappedAccounts) {
        return Promise.all(mappedAccounts);
      }
    } catch (e) {
      showToast('Invalid signer address', 'error', undefined, 'top-right');
    }
  };

  const createAccount = async () => {
    try {
      const mappedAccounts = await mapAccountsData();

      let transactionId;
      const publicKeys = mappedAccounts?.map((account: Signer) => PublicKey.fromString(account.accountKey));

      if (connectedNetwork?.startsWith('custom-')) {
        const config = getConfig(connectedNetwork);
        if (!config) {
          showToast('Not authenticated on custom network', 'error', undefined, 'top-right');
          return;
        }

        const confirmed = await requestSignConfirmation({
          type: 'Create Multisig',
          items: [
            { label: 'Name', value: createAccountData.name || '' },
            { label: 'Threshold', value: `${createAccountData.threshold} of ${mappedAccounts?.length} signers` },
          ],
        });
        if (!confirmed) return;

        // Get active key AFTER confirmation (freshly decrypted by signing modal)
        const activeKey = getActiveKey();
        if (!activeKey) {
          showToast('Key not available — please try again', 'error', undefined, 'top-right');
          return;
        }

        const strategy = new LocalKeyStrategy(activeKey, accountId, config);
        transactionId = await createMultisigTx(
          strategy,
          publicKeys,
          createAccountData.threshold as number,
          createAccountData.name,
        );
      } else {
        // Public network: use WalletConnect
        transactionId = await hashconnectConnectorInstance.createMultisigAccount(
          publicKeys,
          createAccountData.threshold as number,
          accountId,
          connectedNetwork!,
          createAccountData.name,
        );
      }

      setIsLoading(true);
      const walletPayload = {
        ...createAccountData,
        network: connectedNetwork,
        accounts: mappedAccounts,
        transactionId: transactionId.toString(),
      };
      await createMultisigAccount(walletPayload);
      navigate(ApplicationRoutes.Base);
      showToast('Successfully created', 'success', undefined, 'top-right');
    } catch (err: unknown) {
      const error = err as AxiosError;
      const errorMessage = error?.message || String(err);
      if (
        errorMessage.toLowerCase().includes('reject') ||
        errorMessage.toLowerCase().includes('cancel')
      ) {
        showToast('Transaction rejected', 'error', undefined, 'top-right');
        return;
      }
      // Says whether the account was created, so a retry doesn't make a second one
      showToast(getWalletErrorMessage(err, errorMessage), 'error', undefined, 'top-right');
    } finally {
      clearKey();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    updateRouteData();
  }, [location.pathname, updateRouteData]);

  useEffect(() => {
    if (!accountId) {
      navigate(ApplicationRoutes.Base);
    }
  }, [accountId, navigate]);

  useEffect(() => {
    if (location.pathname === ApplicationRoutes.CreateAccount) {
      setIsInvalid(false);
      setIsNextButtonDisabled(!createAccountData.name ? true : false);
    } else if (location.pathname === ApplicationRoutes.SignersForm) {
      if (createAccountData.accounts?.includes(creatorAccount) && createAccountData.accounts.length < 2) {
        setIsNextButtonDisabled(false)
        return
      }
      setIsNextButtonDisabled(missingSignersData ? true : false);
    } else {
      setIsNextButtonDisabled(false);
    }
  }, [location.pathname, createAccountData, missingSignersData, creatorAccount]);


  return (
    <ReactPortal
      id="extras"
      withHeader
      disablePortal={!isScreenSmall}
    >
      <MotionFlex
        h="full"
        flexDirection="column"
        pt={isScreenSmall ? 0 : 6}
        maxW={1098}
        w="full"
        alignItems="stretch"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
      >
        <MotionFlex
          flex={1}
          justifyContent="center"
          variants={fadeUp}
          custom={0}
        >
          {
            !isScreenLarge && (
              <Box mr={4}>
                <Menu />
              </Box>
            )
          }
          <AnimatePresence mode="wait" custom={direction.current}>
            <MotionBox
              key={location.pathname}
              custom={direction.current}
              variants={stepTransition}
              initial="initial"
              animate="animate"
              exit="exit"
              flex={1}
              display="grid"
            >
              {renderComponent()}
            </MotionBox>
          </AnimatePresence>
        </MotionFlex>
        <MotionFlex
          display="flex"
          justifyContent="center"
          px={isScreenSmall ? 4 : 16}
          py={isScreenSmall ? 4 : 12}
          gap={4}
          variants={fadeUp}
          custom={0.15}
        >
          {
            location.pathname === ApplicationRoutes.CreateAccount
              ? (
                <Button
                  maxW={204}
                  w="full"
                  variant="primary"
                  isLoading={isLoading}
                  borderRadius={6}
                  onClick={() => navigate(ApplicationRoutes.Base)}
                >
                  Cancel
                </Button>
              )
              : (
                <Button
                  maxW={204}
                  w="full"
                  variant="primary"
                  borderRadius={6}
                  isLoading={isLoading}
                  onClick={() => navigate(backButtonRoute)}
                >
                  Back
                </Button>
              )
          }


          <Text
            sx={styles.steps(isScreenSmall)}
            whiteSpace="nowrap"
          >
            Step {step} of 4
          </Text>

          {
            location.pathname !== ApplicationRoutes.SummaryForm
              ? (
                <Button
                  maxW={204}
                  w="full"
                  variant="primary"
                  borderRadius={6}
                  onClick={() => navigate(nextButtonRoute)}
                  isDisabled={isNextButtonDisabled || isInvalid}
                >
                  Next
                </Button>
              )
              : (
                <Button
                  maxW={204}
                  w="full"
                  variant="primary"
                  borderRadius={6}
                  isLoading={isLoading}
                  isDisabled={isDuplicate || isInvalid}
                  onClick={createAccount}
                >
                  Create
                </Button>
              )
          }
        </MotionFlex>
      </MotionFlex>
    </ReactPortal>
  );
};

export default CreateAccountPage;
